import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type {
  EditTripSegment,
  EditTripStep,
} from '@apps/main/new-desktop/modules/editTripModal';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import { isStayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  getEndLocationSegment,
  getStartingLocationSegment,
} from '@apps/main/new-desktop/pages/carTrip/stores/carTripConfigurationPanelStore.helper';

import type { AppTabType } from '@libs/ui';

import { getEditTripSegmentFromStayIngredient } from '../editTrip/editTripStoreLogic';
import { useUserLocationStore } from '../userLocation';
import { useBrowserStore } from '../browserStore';
import { usePaxStore } from '../pax/paxStore';
import { useEditTripStore } from '../editTrip/editTripStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useMapStore } from '../mapStore/mapStore';

import type {
  ConfigurationEditCarTripDetails,
  ConfigurationEditTripDetails,
  ConfigurationPanelPaxDetails,
} from './configurationPanelStore.model';
import { TabKey } from './configurationPanelStore.model';

export const useConfigurationPanelStore = defineStore(
  'ConfigurationPanelStore',
  () => {
    const browserStore = useBrowserStore();
    const paxStore = usePaxStore();
    const userLocationStore = useUserLocationStore();
    const mapStore = useMapStore();
    const recipeStore = useRecipeStore();
    const editTripStore = useEditTripStore();

    const { t } = i18n.global;
    // State
    const tabSelected = ref<TabKey>(TabKey.Itinerary);
    const editing = ref<boolean>(false);
    const currentStep = ref<EditTripStep>(editTripStore.initialStep);

    const destinationsTab = {
      title: t('planner.configuration_panel.tabs.destinations'),
      icon: 'itinerary' as const,
      key: TabKey.Itinerary,
    };
    const travelersTab = {
      title: t('planner.configuration_panel.tabs.travelers'),
      icon: 'people' as const,
      key: TabKey.Pax,
    };
    const departureDateTab = {
      title: t('planner.configuration_panel.tabs.departure_date'),
      icon: 'calendarOutlined' as const,
      key: TabKey.Calendar,
    };

    // Actions
    function changeEditStep(value: EditTripStep): void {
      currentStep.value = value;
    }

    function handleTabSwitch(tabKey: TabKey): void {
      tabSelected.value = tabKey;
    }

    function updateEditing(isEditing: boolean): void {
      editing.value = isEditing;
    }

    // Getters
    const getPaxDetails = computed<ConfigurationPanelPaxDetails | undefined>(
      () => ({
        title: paxStore.getPaxesButtonSimpleText,
        initialPaxes: paxStore.initialPaxes,
        shouldShowRoomSelectionWarning: paxStore.shouldShowRoomSelectionWarning,
        shouldShowTicketsWarning: paxStore.shouldShowTicketsWarning,
        shouldShowNumberOfVehiclesUpdatedWarning:
          paxStore.shouldShowNumberOfVehiclesUpdatedWarning,
        shouldShowNumberOfVehiclesWillBeUpdatedWarning: false,
      })
    );

    const getTabSelected = computed<TabKey>(() => tabSelected.value);

    const getCarTripConfigurationPanelTabs = computed<AppTabType[]>(() => [
      {
        title: `${t('planner.configuration_panel.tabs.destinations')}`,
        icon: 'itinerary',
        key: TabKey.Itinerary,
      },
      {
        title: t('planner.configuration_panel.tabs.travelers'),
        icon: 'peopleOutlined',
        key: TabKey.Pax,
      },
      {
        title: t('planner.configuration_panel.tabs.date'),
        icon: 'calendarOutlined',
        key: TabKey.Calendar,
      },
    ]);

    function getConfigurationPanelTabs(showFlightTab: boolean): AppTabType[] {
      const flyFromTab = {
        title: t('planner.configuration_panel.tabs.fly_from'),
        icon: 'airplaneTakeoff' as const,
        key: TabKey.Airplane,
      };

      const tabs: AppTabType[] = [
        destinationsTab,
        departureDateTab,
        travelersTab,
      ];

      if (showFlightTab) {
        tabs.push(flyFromTab);
      }

      return tabs;
    }

    const editTripCarDetails = computed<
      ConfigurationEditCarTripDetails | undefined
    >(() => {
      const startingLocationSegment: EditTripSegment =
        getStartingLocationSegment(
          userLocationStore.startLocation,
          mapStore.calculatedRoute[0]
        );

      const segments = recipeStore.recipe
        .filter(isStayIngredient)
        .map((stay, index) =>
          getEditTripSegmentFromStayIngredient(
            stay,
            mapStore.calculatedRoute[index + 1]
          )
        );

      const endLocationSegment: EditTripSegment = getEndLocationSegment(
        userLocationStore.endLocation
      );
      return {
        segments: [startingLocationSegment, ...segments, endLocationSegment],
        initialStep: currentStep.value,
      };
    });

    const editTripDetails = computed<ConfigurationEditTripDetails | undefined>(
      () => ({
        segments: editTripStore.modalData,
        isDesktop: browserStore.isSmallerThan600px,
        initialStep: currentStep.value,
      })
    );

    const getCurrentStep = computed<EditTripStep>(() => currentStep.value);

    const isEditing = computed<boolean>(() => editing.value);

    return {
      // Actions
      handleTabSwitch,
      updateEditing,
      changeEditStep,

      // Getters
      getCurrentStep,
      getPaxDetails,
      getTabSelected,
      getCarTripConfigurationPanelTabs,
      getConfigurationPanelTabs,
      editTripCarDetails,
      editTripDetails,
      isEditing,
    };
  }
);
